// ═══════════════════════════════════════════════════════════════════
// portal3D.jsx — THE FIVE DOORS, THEIR STONE BUILT IN BLENDER   (caP'n, 2026-09-22)
// ═══════════════════════════════════════════════════════════════════
// public/models/plaza_portal.glb is ONE arch off plaza_1.webp — dressed stone,
// a pointed outer arch over the round door, the keystone and its crown, the
// rune panels. Neutral grey: each door TINTS it with its own colour, and
// paints its own runes (slot_runes, slot_arcrunes) and sigil (slot_sigil),
// exactly as the painting colours each arch. Placement, the vortex, the maw,
// the halo, the pool and the one useFrame are still <Portals/>'s — this only
// hands it a Frame. Fail-safe: the primitive arches.
// ═══════════════════════════════════════════════════════════════════
import { Component, Suspense, useMemo } from "react";
import { useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { Portals, runeStripTex, sigilTex } from "./plazaPieces3D";
import { prepareProp, propUrl } from "./plazaProp3D";
import { usePaintedBodies } from "./sculptGate";

const URL = propUrl("plaza_portal.glb");
const BUILT_W = 4.32;                 // the arch was built for a 6-wide zone (W = 6 x 0.72)

function ArchFrame({ p, W }) {
  const gltf = useLoader(GLTFLoader, URL);
  const parts = useMemo(() => {
    const runes = runeStripTex(p.color);
    const arc = runes.clone(); arc.wrapS = arc.wrapT = THREE.RepeatWrapping; arc.repeat.set(1, 4); arc.needsUpdate = true;
    return prepareProp(gltf.scene, {
      tint: p.color, tintMix: 0.72, shade: p.locked ? 0.44 : 0.52,   // each arch is its door's colour in DARK stone, as painted
      slots: { runes, arcrunes: arc, sigil: sigilTex(p.id, p.color) },
    });
  }, [gltf, p.color, p.id, p.locked]);
  return <group scale={W / BUILT_W}><primitive object={parts.root} /></group>;
}

class GlbBoundary extends Component {
  constructor(q) { super(q); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn("[plaza] plaza_portal.glb did not load — the primitive arches stand in", e); }
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

export function SculptedPortals(props) {
  const painted = usePaintedBodies();
  const fb = <Portals {...props} />;
  if (painted) return fb;
  return (
    <GlbBoundary fallback={fb}>
      <Suspense fallback={fb}><Portals {...props} Frame={ArchFrame} /></Suspense>
    </GlbBoundary>
  );
}
export default SculptedPortals;
